import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import { useParams, useSearchParams } from "react-router-dom";
import moment from "moment";

import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import Button from "components/v2/Button/Button";
import usePermissions from "hooks/usePermissions";
import ShipperQuotationFooterActions from "pages/shipper/ShipmentQuotesV2/components/QuotationCard/QuotationFooterActions/QuotationFooterActions";
import ProviderQuotationFooterActions from "pages/provider/ShipmentQuotesV2/components/QuotationCard/QuotationFooterActions";
import { formatDurationFromSeconds } from "../../../helpers/ShipmentQuotesV2Helpers";
import QuotationFooterStatus from "./QuotationFooterStatus";

interface QuotationFooterProps {
  quotation: any;
  showViewDetailsButton?: boolean;
}

export default function QuotationFooter({ quotation, showViewDetailsButton }: QuotationFooterProps) {
  const { id } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const { permissions } = usePermissions();
  const { user_type } = JSON.parse(permissions || "{}");
  const isShipper = user_type === "shipper";
  const isProvider = user_type === "provider";

  const { general, status, reference } = quotation;
  const { quote_expiration_seconds_remaining, quote_expiration } = general || {};

  const isExpired = quote_expiration_seconds_remaining !== null && quote_expiration_seconds_remaining <= 0;
  const isAboutToExpire =
    quote_expiration_seconds_remaining !== null &&
    quote_expiration_seconds_remaining > 0 &&
    quote_expiration_seconds_remaining < 86400;

  const expiresIn = formatDurationFromSeconds(quote_expiration_seconds_remaining);

  const { data: shipmentData }: any = useGetShipmentBasic(id!, {
    enabled: !!id,
    refetchOnWindowFocus: true,
  });
  const { related_shipments, shipment } = shipmentData || {};
  const offPlatformShipment = shipment?.is_off_platform_shipment || false;
  const requestStatus = shipment?.request_status;
  const hasNoShipment = !related_shipments?.length || related_shipments[0]?.relation === "Child";

  const showShipperQuotationActions =
    isShipper &&
    !offPlatformShipment &&
    hasNoShipment &&
    !["Canceled", "Auto canceled"].includes(requestStatus) &&
    !["confirmation_needed", "canceled", "canceled_by_provider", "booked", "booked_as_duplicate", "lost"].includes(status);

  const showProviderQuotationActions =
    isProvider &&
    hasNoShipment &&
    !["canceled", "canceled_by_provider", "lost", "booked", "booked_as_duplicate"].includes(status);

  const handleViewDetailsClick = () => {
    if (!reference) return;
    searchParams.set("detail", reference);
    setSearchParams(searchParams);
  };

  return (
    <div className="quotation-card-footer">
      <QuotationFooterStatus quotation={quotation} />
      <div className="quotation-card-right-side">
        {!isExpired ? (
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip id="tooltip-quotation-expiration">
                <span>{moment(quote_expiration).format("MMMM Do YYYY, h:mm:ss")}</span>
              </Tooltip>
            }
          >
            <div className={`${isAboutToExpire ? "text-warning-primary" : "text-secondary-v2"} font-regular`}>
              <FormattedMessage
                id="quote.listing.expiresIn"
                defaultMessage="Expires in {time}"
                values={{ time: expiresIn }}
              />
            </div>
          </OverlayTrigger>
        ) : (
          <div className="text-error-primary font-regular">
            <FormattedMessage id="quote.quoteExpired" defaultMessage="Quote expired" />
          </div>
        )}

        <div className="quotation-card-actions">
          {showViewDetailsButton && (
            <Button label="View details" variant="secondary-gray" onClick={handleViewDetailsClick} />
          )}
          {showShipperQuotationActions && <ShipperQuotationFooterActions quotation={quotation} />}
          {showProviderQuotationActions && <ProviderQuotationFooterActions quotation={quotation} />}
        </div>
      </div>
    </div>
  );
}

QuotationFooter.defaultProps = {
  showViewDetailsButton: true,
};
